import { create } from 'zustand';
import type { AuthSession, AuthUser } from '@/types';
import { authApi, configureApiAuth } from '@/lib/api-client';

/**
 * Client-side auth/session state (mirrors @bundle/auth's AuthProvider).
 *
 * SECURITY: the access token and csrf token live in memory ONLY — never in
 * localStorage. The 30d refresh token is an httpOnly cookie owned by the BFF;
 * this store never sees it. On boot we attempt a silent refresh to rehydrate.
 */

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  csrfToken: string | null;
  expiresAt: number; // epoch ms
  status: 'idle' | 'loading' | 'authenticated' | 'anonymous';

  // selectors
  isAuthenticated: () => boolean;
  getAccessToken: () => string | null;
  getCsrfToken: () => string | null;

  // actions
  setSession: (session: AuthSession) => void;
  clear: () => void;
  login: (email: string, password: string) => Promise<void>;
  signup: (
    email: string,
    password: string,
    displayName: string,
  ) => Promise<void>;
  logout: () => Promise<void>;
  /** Single-flight-safe refresh used by the api-client on 401. */
  ensureAccessToken: (force?: boolean) => Promise<AuthSession | null>;
  /** Silent rehydrate on app boot. */
  bootstrap: () => Promise<void>;
}

// 60s safety margin before the 15min access token nominally expires.
const EXPIRY_MARGIN_MS = 60_000;

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  accessToken: null,
  csrfToken: null,
  expiresAt: 0,
  status: 'idle',

  isAuthenticated: () => get().status === 'authenticated' && !!get().accessToken,
  getAccessToken: () => get().accessToken,
  getCsrfToken: () => get().csrfToken,

  setSession: (session) =>
    set({
      user: session.user,
      accessToken: session.accessToken,
      csrfToken: session.csrfToken,
      expiresAt: session.expiresAt,
      status: 'authenticated',
    }),

  clear: () =>
    set({
      user: null,
      accessToken: null,
      csrfToken: null,
      expiresAt: 0,
      status: 'anonymous',
    }),

  login: async (email, password) => {
    set({ status: 'loading' });
    try {
      const session = await authApi.login({ email, password });
      get().setSession(session);
    } catch (err) {
      set({ status: 'anonymous' });
      throw err;
    }
  },

  signup: async (email, password, displayName) => {
    set({ status: 'loading' });
    try {
      const session = await authApi.signup({ email, password, displayName });
      get().setSession(session);
    } catch (err) {
      set({ status: 'anonymous' });
      throw err;
    }
  },

  logout: async () => {
    try {
      await authApi.logout();
    } finally {
      get().clear();
    }
  },

  ensureAccessToken: async (force = false) => {
    const { accessToken, expiresAt } = get();
    const stillValid =
      !!accessToken && Date.now() < expiresAt - EXPIRY_MARGIN_MS;
    if (!force && stillValid) {
      return {
        user: get().user!,
        accessToken,
        csrfToken: get().csrfToken!,
        expiresAt,
      };
    }
    try {
      const session = await authApi.refresh();
      get().setSession(session);
      return session;
    } catch {
      get().clear();
      return null;
    }
  },

  bootstrap: async () => {
    set({ status: 'loading' });
    const session = await get().ensureAccessToken(true);
    if (!session) set({ status: 'anonymous' });
  },
}));

// Wire the api-client to read tokens / drive refresh / handle logout.
configureApiAuth({
  getAccessToken: () => useAuthStore.getState().getAccessToken(),
  getCsrfToken: () => useAuthStore.getState().getCsrfToken(),
  ensureAccessToken: (force) => useAuthStore.getState().ensureAccessToken(force),
  onUnauthorized: () => useAuthStore.getState().clear(),
});
